import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import adminUserRequests from "../../api/AdminUserRequests";
import roleRequests from "../../api/RoleRequests";
import Loader from "../../components/utils/Loader";
import Navbar from "../../components/dashboard/Navbar";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Shield,
  Save,
  AlertTriangle,
  Loader2,
  Calendar,
} from "lucide-react";

const EditAdmin = () => {
  const navigate = useNavigate();
  const { adminSlug } = useParams();
  const [, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [roles, setRoles] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    role_slug: "",
  });
  const [errors, setErrors] = useState({});
  const [showNoRolesAlert, setShowNoRolesAlert] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("ghana");
  const [adminData, setAdminData] = useState(null);

  // Fetch admin data
  const fetchAdminData = async () => {
    setFetchingData(true);
    try {
      const response = await adminUserRequests.showTeamMember(adminSlug);
      const data = response.data.data;

      if (data.status_code === "000") {
        const admin = data.data;
        setAdminData(admin);

        // Determine country from phone number
        const phone = admin.phone_number || "";
        const country = phone.startsWith("233")
          ? "ghana"
          : phone.startsWith("234")
          ? "nigeria"
          : "ghana";
        setSelectedCountry(country);

        // Extract phone number without country code
        const phoneWithoutPrefix = phone.replace(/^\+?(233|234)/, "");

        setFormData({
          first_name: admin.first_name || "",
          last_name: admin.last_name || "",
          email: admin.email || "",
          phone_number: phoneWithoutPrefix,
          role_slug: admin.role_slug || "",
        });
      } else {
        toast.error(data.reason || "Failed to fetch administrator data");
        navigate("/admins");
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to fetch administrator data");
      navigate("/admins");
    } finally {
      setFetchingData(false);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await roleRequests.getRoles();

      if (response.data.status_code === "000") {
        const rolesData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setRoles(rolesData);

        // Find the role_slug based on the role name from admin data
        if (adminData && adminData.role && rolesData.length > 0) {
          const matchingRole = rolesData.find(
            (role) => role.name === adminData.role
          );
          if (matchingRole) {
            setFormData((prev) => ({
              ...prev,
              role_slug: matchingRole.role_slug,
            }));
          }
        }

        if (rolesData.length === 0) {
          setShowNoRolesAlert(true);
        }
      } else {
        toast.error(response.data.reason || "Failed to fetch roles");
        setRoles([]);
        setShowNoRolesAlert(true);
      }
    } catch (error) {
      toast.error("Failed to fetch roles");
      setRoles([]);
      setShowNoRolesAlert(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSlug]);

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".role-dropdown-container")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    } else if (formData.first_name.trim().length < 2) {
      newErrors.first_name = "First name must be at least 2 characters";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    } else if (formData.last_name.trim().length < 2) {
      newErrors.last_name = "Last name must be at least 2 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else {
      const expectedLength = selectedCountry === "ghana" ? 9 : 10;

      if (formData.phone_number.length !== expectedLength) {
        newErrors.phone_number = `Phone number must be ${expectedLength} digits (excluding country code)`;
      } else if (!/^\d+$/.test(formData.phone_number)) {
        newErrors.phone_number = "Phone number must contain only digits";
      }
    }

    if (!formData.role_slug) {
      newErrors.role_slug = "Please select a role";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle country selection
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    // Clear phone number when country changes
    setFormData((prev) => ({
      ...prev,
      phone_number: "",
    }));
    if (errors.phone_number) {
      setErrors((prev) => ({
        ...prev,
        phone_number: "",
      }));
    }
  };

  const formatPhoneNumber = (value) => {
    const cleanValue = value.replace(/\D/g, "");
    const withoutPrefix = cleanValue.replace(/^(233|234)/, "");

    const maxLength = selectedCountry === "ghana" ? 9 : 10;
    const limitedValue = withoutPrefix.slice(0, maxLength);

    return limitedValue;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    if (field === "phone_number") {
      const formattedValue = formatPhoneNumber(value);
      setFormData((prev) => ({
        ...prev,
        [field]: formattedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    if (roles.length === 0) {
      toast.error("No roles available. Please create a role first.");
      return;
    }

    setSaving(true);
    try {
      // Add country prefix to phone number
      const prefix = selectedCountry === "ghana" ? "233" : "234";
      const phoneWithPrefix = `${prefix}${formData.phone_number}`;

      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: phoneWithPrefix,
        admin_slug: adminSlug,
        role_slug: formData.role_slug,
      };

      const response = await adminUserRequests.updateTeamMember(payload);

      if (response.success) {
        toast.success("Administrator updated successfully!");
        navigate("/admins");
      } else {
        toast.error(response.message || "Failed to update administrator");
      }
    } catch (error) {
      console.error("Error updating admin:", error);
      toast.error("Failed to update administrator");
    } finally {
      setSaving(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (fetchingData) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
          <Loader />
        </div>
      </>
    );
  }

  if (!adminData) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
          <div className="text-center">
            <AlertTriangle className="mx-auto mb-4 w-12 h-12 text-red-500" />
            <h2 className="mb-2 text-xl font-semibold text-gray-900">
              Administrator Not Found
            </h2>
            <p className="mb-4 text-gray-600">
              The administrator you're looking for doesn't exist.
            </p>
            <button
              onClick={() => navigate("/admins")}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Back to Admins
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
        <div className="px-4 py-6 mx-auto max-w-6xl sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-8"
          >
            <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-3 items-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/admins")}
                  className="p-2 text-gray-600 bg-white rounded-lg border border-gray-200 transition-colors hover:text-gray-900 hover:bg-gray-50"
                >
                  <ArrowLeft className="w-5 h-5" />
                </motion.button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl lg:text-4xl">
                    Edit Administrator
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 sm:text-base">
                    Update administrator information and permissions
                  </p>
                </div>
              </div>
            </div>

            {/* Current Admin Info Card */}
            <motion.div
              variants={itemVariants}
              className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 shadow-sm sm:p-6"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-sm sm:p-3">
                    <User className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 truncate sm:text-lg">
                      {adminData.first_name} {adminData.last_name}
                    </h3>
                    <div className="flex flex-col gap-2 mt-1 text-xs text-gray-600 sm:flex-row sm:gap-4 sm:text-sm">
                      <div className="flex gap-1 items-center">
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="truncate">{adminData.email}</span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{adminData.role || "Administrator"}</span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>
                          Created:{" "}
                          {new Date(adminData.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`px-2 py-1 rounded-full text-xs font-medium sm:px-3 sm:text-sm ${
                    adminData.status === 1 ||
                    adminData.status === "1" ||
                    adminData.status === "active"
                      ? "text-red-700 bg-red-100"
                      : "text-orange-700 bg-orange-100"
                  }`}
                >
                  {adminData.status === 1 ||
                  adminData.status === "1" ||
                  adminData.status === "active"
                    ? "Active"
                    : "Inactive"}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* No Roles Alert */}
          {showNoRolesAlert && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 mb-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl border border-orange-200 shadow-sm"
            >
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 p-2 bg-orange-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-lg font-semibold text-orange-900">
                    No Roles Available
                  </h3>
                  <p className="mb-4 text-orange-700">
                    You need to create at least one role before editing
                    administrators. Administrators must be assigned to a role to
                    access the system.
                  </p>
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/roles/add")}
                      className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg transition-colors hover:bg-orange-700"
                    >
                      <Shield className="w-4 h-4" />
                      Create Role
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/roles")}
                      className="px-4 py-2 text-sm font-medium text-orange-700 bg-white rounded-lg border border-orange-300 transition-colors hover:bg-orange-50"
                    >
                      View Roles
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Main Form */}
          <motion.div
            variants={itemVariants}
            className="overflow-visible bg-white rounded-2xl border border-gray-100 shadow-xl"
          >
            {/* Form Header */}
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100 sm:p-8">
              <div className="flex gap-3 items-center">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Update Information
                  </h2>
                  <p className="text-sm text-gray-600">
                    Modify the administrator's details and role assignment
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="overflow-visible p-6 sm:p-8"
            >
              <div className="grid overflow-visible grid-cols-1 gap-6 sm:grid-cols-2">
                {/* First Name */}
                <div className="sm:col-span-1">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <User className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) =>
                        handleInputChange("first_name", e.target.value)
                      }
                      className={`pl-10 pr-4 py-3 w-full rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.first_name
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 hover:border-gray-400 bg-white"
                      }`}
                      placeholder="Enter first name"
                    />
                  </div>
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.first_name}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div className="sm:col-span-1">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <User className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) =>
                        handleInputChange("last_name", e.target.value)
                      }
                      className={`pl-10 pr-4 py-3 w-full rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.last_name
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 hover:border-gray-400 bg-white"
                      }`}
                      placeholder="Enter last name"
                    />
                  </div>
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.last_name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="sm:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Mail className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      className={`pl-10 pr-4 py-3 w-full rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.email
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 hover:border-gray-400 bg-white"
                      }`}
                      placeholder="Enter email address"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Country Selection */}
                <div className="sm:col-span-2">
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Country *
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => handleCountryChange("ghana")}
                      className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                        selectedCountry === "ghana"
                          ? "border-blue-500 bg-blue-50 shadow-md"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <div className="flex gap-3 items-center">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedCountry === "ghana"
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-300"
                          }`}
                        >
                          {selectedCountry === "ghana" && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="flex gap-2 items-center mb-1">
                            <div className="flex justify-center items-center w-6 h-6 text-xs font-bold text-white bg-gradient-to-br from-yellow-400 to-red-600 rounded-full">
                              GH
                            </div>
                            <span className="font-medium text-gray-900">
                              Ghana
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            +233 • 9 digits
                          </p>
                        </div>
                      </div>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => handleCountryChange("nigeria")}
                      className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                        selectedCountry === "nigeria"
                          ? "border-red-500 bg-red-50 shadow-md"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <div className="flex gap-3 items-center">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedCountry === "nigeria"
                              ? "border-red-500 bg-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          {selectedCountry === "nigeria" && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="flex gap-2 items-center mb-1">
                            <div className="flex justify-center items-center w-6 h-6 text-xs font-bold text-red-600 bg-gradient-to-br from-red-500 to-white rounded-full">
                              NG
                            </div>
                            <span className="font-medium text-gray-900">
                              Nigeria
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            +234 • 10 digits
                          </p>
                        </div>
                      </div>
                    </motion.button>
                  </div>
                </div>

                {/* Phone Number */}
                <div className="sm:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Phone className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex absolute inset-y-0 left-10 items-center pointer-events-none">
                      <span className="text-sm font-medium text-gray-500">
                        +{selectedCountry === "ghana" ? "233" : "234"}
                      </span>
                    </div>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) =>
                        handleInputChange("phone_number", e.target.value)
                      }
                      className={`pl-20 pr-4 py-3 w-full rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.phone_number
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 hover:border-gray-400 bg-white"
                      }`}
                      placeholder={
                        selectedCountry === "ghana"
                          ? "Enter 9 digits"
                          : "Enter 10 digits"
                      }
                      maxLength={selectedCountry === "ghana" ? 9 : 10}
                    />
                  </div>
                  {errors.phone_number && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.phone_number}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Format: +{selectedCountry === "ghana" ? "233" : "234"}{" "}
                    followed by {selectedCountry === "ghana" ? "9" : "10"}{" "}
                    digits
                  </p>
                </div>

                {/* Role Selection */}
                <div className="relative sm:col-span-2 role-dropdown-container">
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Role Assignment *
                  </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Shield className="w-5 h-5 text-gray-400" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      disabled={roles.length === 0}
                      className={`pl-10 pr-10 py-3 w-full text-left rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.role_slug
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 hover:border-gray-400 bg-white"
                      } ${
                        roles.length === 0
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      {formData.role_slug ? (
                        <span className="text-gray-900">
                          {
                            roles.find(
                              (r) => r.role_slug === formData.role_slug
                            )?.name
                          }
                        </span>
                      ) : (
                        <span className="text-gray-500">Select a role</span>
                      )}
                    </button>
                    <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Dropdown Options - Regular dropdown with better positioning */}
                  {isDropdownOpen && roles.length > 0 && (
                    <div className="absolute z-[9999] w-full top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      {roles.map((role) => (
                        <button
                          key={role.role_slug}
                          type="button"
                          onClick={() => {
                            handleInputChange("role_slug", role.role_slug);
                            setIsDropdownOpen(false);
                          }}
                          className="px-4 py-3 w-full text-left border-b border-gray-100 transition-colors hover:bg-blue-50 last:border-b-0"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-900">
                              {role.name}
                            </span>
                            <span className="px-2 py-1 text-sm text-blue-600 bg-blue-100 rounded-full">
                              {role.permissions?.length || 0} permissions
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {errors.role_slug && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.role_slug}
                    </p>
                  )}
                  {roles.length === 0 && (
                    <p className="mt-1 text-sm text-orange-600">
                      No roles available. Please create a role first.
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 mt-8 sm:flex-row sm:justify-end sm:items-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => navigate("/admins")}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={saving || roles.length === 0}
                  className="flex gap-2 justify-center items-center px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Update Administrator
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default EditAdmin;
